import { Injectable, OnInit } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AppService } from 'app/app.service';
import { environment } from 'environments/environment';
import { EmpresaHttpService } from './empresa-http.service';
import { EmpresaConfig } from 'app/interfaces/empresa-config';
import { SessionStorageService } from 'app/utils/session-storage.service';
import { Title } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root'
})
export class EmpresaConfigService { 
  private configURL = `${environment.s3_url}/EMPRESA/${this.empresaHostname}/config.json`;

  empresaLogo$ = new BehaviorSubject<string>(null);
  empresaConfig$ = new BehaviorSubject<EmpresaConfig>(null);

  constructor(
    private _title: Title,
    private _appService: AppService,
    private _empresaHttpService: EmpresaHttpService,
    private _serviceStorage: SessionStorageService,
  ) {
    this._appService.getUsuarioSubject().subscribe((usuario) => {
      if (usuario?.empresa?.empId) {
        this._empresaHttpService.getLogo(usuario.empresa.empId).subscribe((logo) => {
          this.empresaLogo$.next(logo);
        })
      }
    });

    this.subscribeEmpresaConfig();
  }

  get empresaHostname() {
    return window.location.hostname;
  }

  setEmpresaLogo(empresaLogo: string) {
    this.empresaLogo$.next(empresaLogo);
  }

  getEmpresaLogoSubject() {
    return this.empresaLogo$.asObservable();
  }

  private subscribeEmpresaConfig() {
    this.empresaConfig$.subscribe((config) => {
      if (!config) {
        const sessionConfig = this._serviceStorage.getItem('config');
        if (sessionConfig) {
          config = (JSON.parse(sessionConfig) as { data: number, config: EmpresaConfig }).config;
        }
      }

      if (config) {
        this.loadFaviconETitulo(config);
      }
    })
  }

  private empresaConfigPadrao() {
    return { 
      backgroundURL: undefined,
      logoURL: undefined,
      nomeEmpresa: "LOGZ - Movez",
      icon: './assets/images/ico/faviconlogz.ico'
    } as EmpresaConfig;
  }

  private setConfigPadroes(config: EmpresaConfig) {
    const configPadrao = this.empresaConfigPadrao();

    config.backgroundURL = config.backgroundURL ?? configPadrao.backgroundURL;
    config.logoURL = config.logoURL ?? configPadrao.logoURL;
    config.nomeEmpresa = config.nomeEmpresa ?? configPadrao.nomeEmpresa;
    config.icon = config.icon ?? configPadrao.icon;

    return config;
  }

  private isCacheValido(cacheConfig: string): boolean {
    const configSession = JSON.parse(cacheConfig) as { data: number, config: EmpresaConfig };
    const CACHE_DURACAO = 48 * 60 * 60 * 1000; // 48 horas em ms
    const now = Date.now();
    
    return configSession.data && (now - configSession.data) < CACHE_DURACAO;
  }

  loadFaviconETitulo(config: EmpresaConfig) {
    this._title.setTitle(config.nomeEmpresa);
    const icon = document.querySelector("head link[rel=icon][type='image/x-icon']") as HTMLLinkElement;
    icon.href = config.icon;
  }

  loadEmpresaConfig() {
    return new Observable<EmpresaConfig>((subs) => {
      const empresaConfigStorage = this._serviceStorage.getItem('config');

      if (empresaConfigStorage && this.isCacheValido(empresaConfigStorage)) {
        const configSession = JSON.parse(empresaConfigStorage) as { data: number, config: EmpresaConfig };
        configSession.config = this.setConfigPadroes(configSession.config);
        this.empresaConfig$.next(configSession.config);
        subs.next(configSession.config);
      } else {
        fetch(this.configURL, { method: 'GET' })
        .then((res) => res.status === 200 ? res.json() : undefined )
        .then((body) => { 
          if (!body) {
            body = this.empresaConfigPadrao();
          } else {
            body = this.setConfigPadroes(body);
          }

          this._serviceStorage.setItem("config", JSON.stringify({ data: Date.now(), config: body }));
          this.empresaConfig$.next(body);
          subs.next(body);
        })
      }
    });
  }
}